<template>
	<A-sidemenu
		v-model="filters"
		:selected-patient-id="selectedPatientId"
		:patients="patients"
		:loading="loading"
		@select-patient="selectPatient"
		@close="close"
	/>

	<ion-page>
		<ion-header>
			<ion-toolbar>
				<div class="sidemenu-button" slot="start">
					<ion-icon :icon="icons.menuOutline" @click="openSidemenu()"></ion-icon>
				</div>
				<ion-title>
					<div class="flex items-center">
						{{ $t('Výsledkové listy') }}

						<span
							v-if="!filters?.updated_from && !filters?.last_result?.start"
							class="font-normal ml-[5px]"
						>
							{{ $t('pre celé obdobie ') }}
						</span>
						<span v-else class="font-normal ml-[5px]">
							{{
								$t('pre obdobie') +
								'&nbsp;' +
								(filters?.updated_from
									? filters?.updated_from + ' - ' + filters?.updated_to
									: filters?.last_result?.start +
									  ' - ' +
									  filters?.last_result?.end)
							}}
						</span>
						<Z-icon-button
							class="calendar-icon"
							:icon="icons.calendar"
							@click="close"
							>{{ $t('Zmeniť obdobie') }}</Z-icon-button
						>
					</div>
				</ion-title>

				<div v-if="barcode" class="menu-title">
					<span class="font-bold ml-1">{{ $t('Žiadanka') }}</span>
					<span class="ml-1.5 font-normal"> {{ barcode }}</span>
				</div>

				<ion-icon class="close-icon" :src="icons.closeOutline" slot="end" @click="close" />
			</ion-toolbar>
		</ion-header>

		<ion-content
			:scroll-events="true"
			@ion-scroll="onScroll($event)"
			ref="ionContent"
			data-cy="results-detail"
		>
			<div class="modal" ref="modal">
				<A-patients-list
					class="requests-list"
					v-model="filters"
					:pagination="pagination"
					:selected-patient-id="selectedPatientId"
					:patients="patients"
					:loading="loading"
					@select-patient="selectPatient"
					@next-page="loadMoreRequests"
				/>

				<div class="modal__content">
					<template v-if="patientRequests">
						<div
							v-for="(request, requestOrderId, index) in patientRequests"
							:key="`request-${requestOrderId}`"
						>
							<template v-if="request._is_opened">
								<div
									class="request"
									:class="{ opened: request._is_opened }"
									:ref="`request-${requestOrderId}`"
								>
									<A-request-header
										:request="request._full_request"
										@toggle-read-unread="toggleReadUnread"
									/>

									<A-request-info
										:request="request._full_request"
										:patient-id="
											request?._full_request?.order?.request?.patient_id
										"
									/>

									<A-request-result
										v-for="(result, index) in $skelet(
											request._full_request?.results,
											2
										)"
										:key="index"
										:request="request._full_request"
										:result="result"
										:patient="request._full_request?.patient"
										:order-external-id="request?._full_request?.order_id"
									/>
								</div>
								<ion-toolbar
									v-if="
										index == 0 && Object.keys(this.patientRequests).length > 1
									"
									class="intertitle"
								>
									<ion-title>{{ $t('Predchádzajúce žiadanky') }}</ion-title>
								</ion-toolbar>
							</template>

							<template v-else>
								<div
									class="request"
									:class="{ opened: request._is_opened }"
									:ref="`request-${requestOrderId}`"
								>
									<A-previous-request
										:request="request"
										@open-request="({ order_id }) => selectRequest(order_id)"
									/>
								</div>
							</template>
						</div>
						<ion-toolbar class="ion-text-center" v-if="showMore">
							<div
								v-tooltip.top-center="{
									content: (
										this.patients[this.selectedPatientId]?.pagination
											? this.patients[this.selectedPatientId]?.pagination
													?.has_next_page
											: true
									)
										? $t('Zobraziť všetky')
										: $t('Všetky výsledky boli zobrazené'),
									theme: 'info-tooltip'
								}"
							>
								<Z-icon-button
									:loading="this.patients[this.selectedPatientId]?.loading"
									:disabled="
										this.patients[this.selectedPatientId]?.pagination
											? !this.patients[this.selectedPatientId]?.pagination
													?.has_next_page
											: false
									"
									@click="loadPatientHistory"
									>{{ $t('Načítať viac') }}</Z-icon-button
								>
							</div>
						</ion-toolbar>
					</template>
				</div>
			</div>

			<!-- <A-previous-requests v-if="previousPatientRequests" :requests="previousPatientRequests" :selectedRequest="selectedRequest" /> -->

			<ion-fab
				v-show="pageScroll.hasReachedBottom"
				slot="fixed"
				vertical="bottom"
				horizontal="end"
			>
				<ion-fab-button @click="scrollToTop()" class="scroll">
					<ion-icon :icon="icons.arrowUp"></ion-icon>
				</ion-fab-button>
			</ion-fab>

			<ion-fab
				v-show="!pageScroll.hasReachedBottom"
				slot="fixed"
				vertical="bottom"
				horizontal="end"
			>
				<ion-fab-button @click="scrollToBottom()" class="scroll">
					<ion-icon :icon="icons.arrowDown"></ion-icon>
				</ion-fab-button>
			</ion-fab>
			<div ref="previous"></div>
		</ion-content>
	</ion-page>
</template>

<script>
import { closeOutline, arrowUp, arrowDown, menuOutline } from 'ionicons/icons'
import { modalController, menuController } from '@ionic/vue'
import { copy, printer, download, seen, calendar } from '@/plugins/@medirex/icons'

import ARequestResult from './_components/a-request-result.vue'
import ARequestHeader from './_components/a-request-header.vue'
import ARequestInfo from './_components/a-request-info.vue'

import APatientsList from './_components/a-patients-list.vue'
import APreviousRequest from './_components/a-previous-request.vue'
import ASidemenu from './_components/a-sidemenu.vue'

import ResultTableMixin from './_mixins/result-table.mixin'

import { uuid } from 'vue-uuid'
import axios from 'axios'

export default {
	mixins: [ResultTableMixin],

	components: {
		ARequestHeader,
		ARequestInfo,
		ARequestResult,
		APreviousRequest,
		ASidemenu,
		APatientsList
	},

	props: {
		presetFilters: {
			type: [Object],
			required: true
		},

		presetPagination: {
			type: [Object],
			default: () => ({ current_page: 1, last_page: 10, per_page: 10 })
		},

		presetRequests: {
			type: [Array, null],
			default: () => null
		},

		requestOrderId: {
			type: [String, Number],
			required: true
		},

		setAsRead: {
			type: [Boolean],
			default: false
		}
	},

	data() {
		return {
			pagination: null,
			loading: false,
			previousPatientRequests: null,
			requests: null,
			filters: null,
			pageScroll: false,

			loading: false,

			selectedPatientId: null,
			selectedRequestOrderId: null,
			scrollSelectedId: 0,

			patients: null,
			request: null,

			requestLoadMoreQuery: null
		}
	},

	setup() {
		return {
			icons: {
				closeOutline,
				download,
				copy,
				calendar,
				seen,
				printer,
				arrowDown,
				arrowUp,
				menuOutline
			}
		}
	},

	computed: {
		patientRequests() {
			return this.selectedPatient?.requests
		},

		selectedPatient() {
			return this.patients?.[this.selectedPatientId]
		},

		sortedPatients() {
			if (!this.patients) return null

			return Object.values(this.patients).sort((a, b) => {
				return new Date(b.last_result_date) - new Date(a.last_result_date)
			})
		},

		barcode() {
			if (!this.patients?.[this.selectedPatientId]?.requests?.[this.selectedRequestOrderId])
				return null

			const selectedRequest =
				this.patients[this.selectedPatientId]?.requests?.[this.selectedRequestOrderId]
			return selectedRequest?._full_request?.examination_request?.barcode
		},

		showMore() {
			// this is for open result from url paramter
			let noMoreLoad = this.patientRequests[this.selectedRequestOrderId]?.moreLoad ?? true

			return (
				this.patientRequests[this.selectedRequestOrderId].patient_id != null && noMoreLoad
			)
		}
	},

	async created() {
		this.filters = JSON.parse(JSON.stringify(this.presetFilters))
		this.pagination = JSON.parse(JSON.stringify(this.presetPagination))
		this._generatePatients(this.presetRequests)

		// We have to find patient by requestOrderId, as he may have generated patient_id (uuid)
		const patient = Object.values(this.patients).find(
			patient => patient.requests[this.requestOrderId]
		)
		this.selectedPatientId = patient.patient_id

		this.selectedRequestOrderId = this.requestOrderId
		if (this.setAsRead) {
			await this.toggleReadUnread()
		}

		this._loadRequest(this.selectedPatientId, this.selectedRequestOrderId)
	},

	async mounted() {
		window.addEventListener('keydown', this.handleKeyPress)
	},

	unmounted() {
		window.removeEventListener('keydown', this.handleKeyPress)
	},

	methods: {
		async _generatePatients(requests) {
			if (!this.patients) this.patients = {}

			for (const request of requests) {
				let patientId = uuid.v1()
				let isExternal = true

				if (request.patient_id) {
					patientId = request.patient_id
					isExternal = false
				}

				// Prevent overwriting of loaded requests
				if (this.patients?.[patientId]?.requests?.[request.order_id]) continue

				if (!this.patients[patientId])
					this.patients[patientId] = {
						last_result_date: request.last_result_date,
						default_order_id: request.order_id,
						personal_id: request.personal_id,
						first_name: request.first_name,
						last_name: request.last_name,
						is_external: isExternal,
						is_readed: request.is_readed,
						patient_id: patientId,
						requests: {}
					}

				this.patients[patientId].requests[request.order_id] = request
			}

			const patientsArray = Object.values(this.patients)
			const filteredPatientsObject = {}
			const orderIds = []
			for (const patient of patientsArray) {
				if (orderIds.includes(patient?.default_order_id)) continue
				filteredPatientsObject[patient.patient_id] = patient
				orderIds.push(patient.default_order_id)
			}

			this.patients = filteredPatientsObject
		},

		async _loadRequest(selectedPatientId, selectedRequestOrderId, isPreload = false) {
			if (!this.patients) return

			if (!isPreload) {
				this.patients[selectedPatientId].requests[selectedRequestOrderId] = {
					...this.patients[selectedPatientId].requests[selectedRequestOrderId],
					_is_opened: true
				}

				this.$nextTick(() => {
					this.$refs[`request-${selectedRequestOrderId}`]?.[0]?.scrollIntoView({
						behavior: 'smooth'
					})
				})
			}

			// Request is already loaded or is loading in the background
			const selectedRequest =
				this.patients[selectedPatientId]?.requests?.[selectedRequestOrderId]
			if (
				!selectedRequest ||
				selectedRequest?.examination_request ||
				selectedRequest?.loading
			)
				return

			try {
				selectedRequest.loading = true
				const query = new URLSearchParams({
					order_external_id: selectedRequestOrderId
				}).toString()
				const request = await this.$axios.get_auth_data(`v1/results?${query}`)

				// If patients were not cleared in the meantime
				if (this.patients?.[selectedPatientId]?.requests?.[selectedRequestOrderId]) {
					// If patients were not cleared in the meantime
					this.request = request
					this.patients[selectedPatientId].requests[
						selectedRequestOrderId
					]._full_request = request
				}
			} catch (error) {
				this.$toast.error(error)
			}
		},

		async loadMoreRequests() {
			try {
				this.loading = true

				this.pagination.current_page += 1
				this.pagination.page = this.pagination.current_page
				delete this.pagination.current_page
				delete this.pagination.last_page
				const payload = { ...this.filters, ...this.pagination }
				const { data: requests, meta: pagination } = await this.$axios.post_auth(
					'v2/requests',
					payload
				)

				this._generatePatients(requests)
				this.pagination = pagination
			} catch (error) {
				this.$toast.error(error)
			} finally {
				this.loading = false
			}
		},

		async loadPatientHistory() {
			if (!this.patients?.[this.selectedPatientId]) return

			try {
				this.patients[this.selectedPatientId].loading = true
				const patientPagination = this.patients[this.selectedPatientId].pagination

				const filter = {
					per_page: 10,
					current_page: patientPagination ? patientPagination.current_page + 1 : 1,
					patient_id: this.selectedPatientId
				}

				const query = new URLSearchParams(filter).toString()
				const { data: requests, meta: pagination } = await this.$axios.get_auth(
					`v1/requests?${query}`
				)

				this._generatePatients(requests)
				this.patients[this.selectedPatientId].pagination = pagination
			} catch (error) {
				this.$toast.error(error)
			} finally {
				if (this.patients?.[this.selectedPatientId])
					this.patients[this.selectedPatientId].loading = false
			}
		},

		async _reloadRequests() {
			const oldPatients = this.patients
			this.patients = null

			try {
				this.pagination = { current_page: 1, per_page: 10 }
				const payload = { ...this.filters, ...this.pagination }
				const { data: requests, meta: pagination } = await this.$axios.post_auth(
					'v2/requests',
					payload
				)

				this._generatePatients(requests)
				this.pagination = pagination

				// If selected patient is no longer available, select the first one
				if (!this.patients[this.selectedPatientId]) {
					const firstPatient = this.sortedPatients[0]

					if (!firstPatient) return

					// If patient is found, select him
					this.selectedPatientId = firstPatient.patient_id
					this.selectedRequestOrderId = firstPatient.default_order_id
					const firstRequestOrderId = Object.keys(firstPatient.requests)[0]

					// If request is found, open it
					if (this.patients[firstPatient.patient_id].requests[firstRequestOrderId])
						this.patients[firstPatient.patient_id].requests[
							firstRequestOrderId
						]._is_opened = true

					// Load first request
					this._loadRequest(this.selectedPatientId, this.selectedRequestOrderId)
				}
			} catch (error) {
				this.$toast.error(error)
				this.patients = oldPatients
			}
		},

		close() {
			window.removeEventListener('keydown', this.handleKeyPress)
			modalController.dismiss({ role: 'dismiss' })
		},

		async openSidemenu() {
			await menuController.open('result-menu')
		},

		selectPatient(patientId) {
			this.selectedPatientId = patientId
			this.selectedRequestOrderId = this.patients[patientId].default_order_id

			this._loadRequest(this.selectedPatientId, this.selectedRequestOrderId)
		},

		selectRequest(requestOrderId) {
			this.selectedRequestOrderId = requestOrderId
			this._loadRequest(this.selectedPatientId, this.selectedRequestOrderId)
		},

		async toggleReadUnread() {
			try {
				const selectedRequestId = this.scrollSelectedId
				const patientRequestsValues = Object.keys(this.patientRequests)

				const selectedRequestOrderId = patientRequestsValues[selectedRequestId]

				this.selectedRequestOrderId = selectedRequestOrderId
				const isReaded =
					!this.patients[this.selectedPatientId]?.requests?.[this.selectedRequestOrderId]
						?.is_readed

				this._immediateReadUnread(isReaded)
				const query = new URLSearchParams({
					order_external_id: this.selectedRequestOrderId,
					is_read: isReaded
				}).toString()
				await this.$axios.patch_auth_data(`v1/results/read?${query}`)
			} catch (error) {
				this.$toast.error(error)
				this._immediateReadUnread(!isReaded)
			}
		},

		_immediateReadUnread(value) {
			if (this.patients[this.selectedPatientId])
				this.patients[this.selectedPatientId].is_readed = value

			if (this.patients[this.selectedPatientId]?.requests?.[this.selectedRequestOrderId])
				this.patients[this.selectedPatientId].requests[
					this.selectedRequestOrderId
				].is_readed = value

			if (
				this.patients[this.selectedPatientId]?.requests?.[this.selectedRequestOrderId]
					?._full_request
			)
				this.patients[this.selectedPatientId].requests[
					this.selectedRequestOrderId
				]._full_request.is_readed = value
		},

		handleKeyPress(event) {
			if (event.target.tagName == 'INPUT') return // Ignroe search key press

			if (event.key === ' ') return this.toggleReadUnread()

			if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

			const patientIds = Object.values(this.sortedPatients).map(obj => obj.patient_id)
			let currentIndex = patientIds.indexOf(this.selectedPatientId)

			if (event.key === 'ArrowUp') {
				if (currentIndex === 0) {
					currentIndex = patientIds.length - 1
				} else {
					currentIndex -= 1
				}
			} else if (event.key === 'ArrowDown') {
				if (currentIndex === patientIds.length - 1) {
					currentIndex = 0
				} else {
					currentIndex += 1
				}
			}
			this.selectPatient(patientIds[currentIndex])
		},

		scrollToBottom() {
			this.$refs.previous.scrollIntoView({ behavior: 'smooth' })
		},

		scrollToTop() {
			this.$refs.modal.scrollIntoView({ behavior: 'smooth' })
		},

		async onScroll(event) {
			const ionContent = this.$refs.ionContent.$el
			// Get the actual scroll element from ion-content
			const scrollElement = ionContent.shadowRoot 
				? ionContent.shadowRoot.querySelector('.inner-scroll') || ionContent.shadowRoot.querySelector('ion-content')
				: ionContent.querySelector('.inner-scroll') || ionContent.querySelector('ion-content') || ionContent
			
			// Get scrollTop from event or element
			const scrollTop = event?.detail?.scrollTop ?? scrollElement?.scrollTop ?? 0
			
			const bottomOffset =
				scrollElement.scrollHeight - scrollTop - scrollElement.clientHeight

			this.pageScroll = {
				hasReachedBottom: bottomOffset < 200
			}

			// Get the container where divs are located
			const contentContainer = ionContent.querySelector('.modal__content')
			if (!contentContainer) return

			const viewportRect = ionContent.getBoundingClientRect()
			const openedDivs = contentContainer.querySelectorAll('div.opened')

			if (!openedDivs.length) {
				this.scrollSelectedId = 0
				return
			}

			// Threshold: 150px from top of viewport
			const thresholdViewportY = viewportRect.top + 150
			// Convert to scroll coordinates
			const thresholdScrollY = scrollTop + 150

			let currentDivIndex = -1
			let bestDivIndex = 0
			let bestScore = Infinity

			for (let i = 0; i < openedDivs.length; i++) {
				const div = openedDivs[i]
				const divRect = div.getBoundingClientRect()
				
				// Calculate div position in scroll coordinates
				// divRect.top is relative to viewport, convert to scroll coordinates
				const divTopInScroll = divRect.top - viewportRect.top + scrollTop
				const divBottomInScroll = divTopInScroll + divRect.height
				
				// Check if threshold point is within this div
				if (thresholdScrollY >= divTopInScroll && thresholdScrollY <= divBottomInScroll) {
					currentDivIndex = i
					break
				}
				
				// Calculate score: distance from threshold, prefer divs above threshold
				let score
				if (divTopInScroll <= thresholdScrollY) {
					// Div is above or at threshold - closer is better
					score = thresholdScrollY - divTopInScroll
				} else {
					// Div is below threshold - add large penalty
					score = (divTopInScroll - thresholdScrollY) + 100000
				}
				
				if (score < bestScore) {
					bestScore = score
					bestDivIndex = i
				}
			}

			// Use the div that contains threshold, or the best match
			this.scrollSelectedId = currentDivIndex >= 0 ? currentDivIndex : bestDivIndex
		}
	},

	watch: {
		filters: {
			handler(value, oldValue) {
				// Skip initial load
				if (!oldValue) return

				this._reloadRequests()
			},
			deep: true
		}
	}
}
</script>

<style lang="sass" scoped>
@import '@/plugins/app@lz/profile/_theme/profile'
@import '@/plugins/app/theme/_mixins/responsive.mixin'

ion-header
	ion-toolbar
		height: 60px
		border-style: none
		--border-width: 0
		box-shadow: 0px 2px 5px rgba(33, 51, 83, 0.10)
		background: white
		display: flex
		justify-content: space-between
		align-items: center

		ion-title
			text-align: left
			font-size: 16px
			padding: 0 71px

			+xl
				@apply hidden

		.close-icon
			color: #2E3C55
			font-size: 20px
			cursor: pointer
			padding: 0 65px

			+md
				@apply px-[16px]

ion-content
	&::part(background)
		@apply bg-[#f4f6f8]

ion-fab-button
	@apply mr-[8px]

.sidemenu-button
	@apply hidden ml-[56px]

	ion-icon
		@apply w-[30px] h-[30px] text-[#6E7D8D] transform translate-x-[-4px] mt-1 pl-2

	+xl
		@apply block

	+md
		@apply ml-0

.requests-list
	@apply fixed

	+xl
		@apply hidden

.status-time-container
	display: flex
	align-items: top
	color: black

.status-icon
	margin-right: 12px

.time
	margin-bottom: 20px
	font-size: 12px
	font-weight: 400
	color: var(--ion-color-medium)

.calendar-icon
	@apply ml-[22px]

.icon
	color: white
	display: table

.menu-title
	@apply text-[14px] flex hidden

	+xl
		@apply block

.modal
	@apply bg-[--ion-color-light] p-[16px] mr-[56px] flex

	+xl
		@apply ml-[56px]

	+md
		@apply mr-0 ml-auto

	&__content
		@apply w-full max-w-full h-full ml-[395px] m-auto

		:deep(.spinner)
			left: 50%!important

		+xl
			@apply ml-0

		+md
			@apply px-[0]

		.intertitle
			--background: none
			@apply my-5

			ion-title
				@apply text-[20px]

		.request
			@apply bg-white mb-[20px] px-[30px] py-[20px]

			&.opened
				@apply pb-[35px] pt-0

	&__buttons
		@apply w-full min-h-[57px] relative flex items-center justify-between

		&__left
			@apply inline-block text-left

			+md
				@apply hidden

		&__right
			@apply inline-block text-right items-end

	&__border
		@apply border-[#E6EBF0] border-b-2 -mr-[30px] -ml-[30px]

		+md
			@apply mx-0

	&__header
		@apply pt-[19px] border-black border-b-2 relative flex items-center min-h-[80px]

		+md
			@apply block

		&__title
			@apply min-[890px]:w-2/3 inline-block

			h2
				@apply pr-[10px] text-[24px] inline-block

		&__creation-dates
			@apply text-[14px] text-[--ion-color-medium] pt-[15px] min-[890px]:pt-0 text-left min-[890px]:text-right min-[890px]:w-1/3 top-5 right-0 min-[890px]:absolute mb-[21px]

			+md
				@apply mt-[-10px]

		&__desc
			@apply text-[18px] -mt-[12px]
</style>
